import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { closeModal, openModal } from "react-url-modal";
import { Button, Modal } from "@/components";
import { TournamentsItem } from "../../components/TournamentsItem";
import { JoinTournamentContent } from "./components/JoinTournamentContent/JoinTournamentContent";
import { ManageTournamentContent } from "./components/ManageTournamentContent";
import { DeleteGameAndTournamentModal } from "@/pages/Games/modals/JoinGameModal/components/DeleteGameAndTournamentModal/DeleteGameAndTournamentModal";
import { CancelRequestModal } from "@/pages/Games/components/CancelRequestModal";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import {
  selectAvailableTournaments,
  selectMyApplicationsTournaments,
  selectMyCreatedTournaments,
  fetchGetAvailableTournaments,
  fetchGetMyApplicationsTournaments,
  fetchGetCreatedTournaments,
  fetchCancelTournamentApplication,
} from "@/redux/tournaments";
import { ITournament } from "@/redux/tournaments/types";
import styles from "./JoinTournamentModal.module.scss";

interface JoinTournamentModalProps {
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  params?: {
    idTournament?: string;
    organizator?: string;
    isApplied?: string;
    fromDeeplink?: string;
  };
}

const JoinTournamentModalComponent = ({
  setIsOpen,
  params = {},
}: JoinTournamentModalProps): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const idTournament = params?.idTournament ? parseInt(params.idTournament, 10) : null;
  const isOrganizator = params?.organizator === "true";
  const isApplied = params?.isApplied === "true";
  const fromDeeplink = params?.fromDeeplink === "true";

  const availableTournaments = useAppSelector(selectAvailableTournaments);
  const myApplicationsTournaments = useAppSelector(selectMyApplicationsTournaments);
  const myCreatedTournaments = useAppSelector(selectMyCreatedTournaments);

  const [tournament, setTournament] = useState<ITournament | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);

  useEffect(() => {
    if (!idTournament) {
      handleClose();
      return;
    }

    const loadTournament = async () => {
      setIsLoading(true);
      try {
        let currentTournament: ITournament | null = null;

        if (isOrganizator) {
          // Ищем в созданных турнирах
          currentTournament = myCreatedTournaments.find((t) => t.id === idTournament) || null;
        } else {
          // Ищем в доступных турнирах
          currentTournament = availableTournaments.find((t) => t.id === idTournament) || null;

          // Если не найден, ищем в турнирах с заявками
          if (!currentTournament) {
            currentTournament = myApplicationsTournaments.find((t) => t.id === idTournament) || null;
          }
        }

        // Если турнир не найден, обновляем списки и ищем снова
        if (!currentTournament) {
          await Promise.all([
            dispatch(fetchGetAvailableTournaments()),
            dispatch(fetchGetMyApplicationsTournaments()),
            dispatch(fetchGetCreatedTournaments()),
          ]);
          // Пробуем найти снова после обновления
          if (isOrganizator) {
            const updatedCreated = await dispatch(fetchGetCreatedTournaments()).unwrap();
            currentTournament = updatedCreated.find((t) => t.id === idTournament) || null;
          } else {
            const updatedAvailable = await dispatch(fetchGetAvailableTournaments()).unwrap();
            const updatedApplied = await dispatch(fetchGetMyApplicationsTournaments()).unwrap();
            currentTournament = updatedAvailable.find((t) => t.id === idTournament) || null;
            if (!currentTournament) {
              currentTournament = updatedApplied.find((t) => t.id === idTournament) || null;
            }
          }
        }

        setTournament(currentTournament);
      } catch (error) {
        console.error("Error loading tournament:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadTournament();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTournament, isOrganizator, myCreatedTournaments, availableTournaments, myApplicationsTournaments]);

  const handleClose = () => {
    if (fromDeeplink) {
      navigate("/padel/tournaments");
    }
    closeModal();
    if (setIsOpen) {
      setIsOpen(false);
    }
  };

  if (isLoading) {
    return (
      <Modal
        title="Загрузка..."
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.loading}>Загрузка турнира...</div>
      </Modal>
    );
  }

  if (!tournament) {
    return (
      <Modal
        title="Ошибка"
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={600}
      >
        <div className={styles.error}>Турнир не найден</div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        title={
          isOrganizator
            ? "Управление турниром"
            : isApplied
            ? "Информация о турнире"
            : "Записаться на турнир"
        }
        isOpen={true}
        setIsOpen={setIsOpen || (() => {})}
        closeModal={handleClose}
        modalWindowWidth={500}
        mobileContentSize="full-screen"
        firmBack
      >
        <div className={styles.content}>
          <div className={styles.tournamentItem}>
            <TournamentsItem
              item={tournament}
              button={false}
              setIsRequiredAuthOpened={() => {}}
              modalWindow
              isOrganizator={isOrganizator}
              onDeleteClick={() => setDeleteModalVisible(true)}
            />
          </div>

          {!isOrganizator && !isApplied && (
            <JoinTournamentContent
              tournament={tournament}
              onLevelInfoClick={(level) => {
                // Можно добавить дополнительную логику если нужно
              }}
            />
          )}

          {isOrganizator && (
            <div className={styles.manageContent}>
              <ManageTournamentContent tournament={tournament} />
            </div>
          )}

          {isApplied && (
            <div className={styles.actions}>
              <Button
                newStyles={styles.buttonTransparent}
                onClickFunc={() => setCancelModalVisible(true)}
              >
                Отменить заявку
              </Button>
            </div>
          )}
        </div>
      </Modal>

      {deleteModalVisible && idTournament && (
        <DeleteGameAndTournamentModal
          isOpen={deleteModalVisible}
          setIsOpen={setDeleteModalVisible}
          tournamentId={idTournament}
          date={tournament?.date}
          onSuccess={handleClose}
        />
      )}

      {cancelModalVisible && idTournament && (
        <CancelRequestModal
          isOpen={cancelModalVisible}
          setIsOpen={setCancelModalVisible}
          isTournament
          onConfirm={async () => {
            try {
              await dispatch(fetchCancelTournamentApplication(idTournament)).unwrap();
              // Обновляем списки
              await Promise.all([
                dispatch(fetchGetMyApplicationsTournaments()),
                dispatch(fetchGetAvailableTournaments()),
              ]);
              handleClose();

              // Открываем модалку успешной отмены заявки
              openModal({ name: "successCancelTournamentRequest" });
            } catch (error) {
              console.error("Error cancelling tournament application:", error);
            }
          }}
          isLoading={false}
        />
      )}
    </>
  );
};

// Экспорт для react-url-modal
export default JoinTournamentModalComponent;
export const JoinTournamentModal = JoinTournamentModalComponent;
